<template>
    <form @submit.prevent="saveColumn()">
        <div class="modal-card">
            <header class="modal-card-head">
                <p class="modal-card-title">{{ isUpdate ? 'Edit' : 'Add' }} Brand Column</p>
            </header>
            <section class="modal-card-body">
                <b-notification type="is-danger" v-if="errorMessage" :closable="false">
                    {{ errorMessage }}
                </b-notification>
                <b-field label="Name">
                    <b-input
                        type="text"
                        v-model="columnData.name"
                        class="column-title-input"
                        required
                    ></b-input>
                </b-field>
                <b-field v-if="!isPublished" label="Netbase Brand Topic">
                    <b-autocomplete
                        v-model="netBaseTopicInput"
                        :data="filteredNetBaseTopics"
                        field="name"
                        v-bind:loading="isNetBaseTopicsLoading"     
                        @select="topic => this.columnData.brandTopic = topic"
                        required
                        open-on-focus
                        clearable
                        placeholder="Select a NetBase Topic"
                        max-height="300px"
                        dropdown-position="bottom"
                    >
                    </b-autocomplete>
                </b-field>
                <b-field v-if="!isPublished">
                    <template slot="label">
                        Netbase Brand Theme <span class="is-italic" style="font-variant:all-petite-caps">Optional</span>
                    </template>
                    <b-autocomplete
                        v-model="netBaseThemesInput"
                        :data="filteredNetBaseThemes"
                        field="name"
                        v-bind:loading="isNetBaseThemesLoading"     
                        @select="theme => this.columnData.brandTheme = theme"
                        open-on-focus
                        clearable
                        placeholder="Select an optional NetBase Theme"
                        max-height="300px"
                        dropdown-position="bottom"
                    >
                    </b-autocomplete>
                </b-field>
                <b-field 
                v-if="!isPublished" 
                label="Included Terms (Share of Conversation)" 
                :type="isIncludedKeywordsValid ? 'is-normal' : 'is-danger'"
                :message="isIncludedKeywordsValid ? '' : 'Keywords cannot include \'\':\'\' (colon) character'">
                    <b-taginput
                        v-model="columnData.keywords"
                        @input="verifyIncludedKeywords()"
                        ellipsis
                        icon="tag fa-xs"
                        placeholder="Add a keyword"
                        class="column-keywords-input"
                    ></b-taginput>
                </b-field>
                <b-field 
                v-if="!isPublished" 
                label="Excluded Terms (Share of Conversation)"
                :type="isExcludedKeywordsValid ? 'is-normal' : 'is-danger'"
                :message="isExcludedKeywordsValid ? '' : 'Keywords cannot include \'\':\'\' (colon) character'">
                    <b-taginput
                        v-model="columnData.excludedKeywords"
                        @input="verifyExcludedKeywords()"
                        ellipsis
                        icon="tag fa-xs"
                        placeholder="Add a keyword"
                        class="column-keywords-input"
                    ></b-taginput>
                </b-field>

                <b-field label="Colour">
                    <verte v-model="columnData.colour" menuPosition="center" :draggable="false" :enableAlpha="false" model="hex"> </verte>
                </b-field>

                <div class="socialtoolsPages">
                    <div class="level">
                        <div class="level-left">
                            <h3 class="title is-size-5">Socialtools Pages</h3>
                        </div>
                        <div class="level-right">
                            <a @click="verifySocialtoolsPages()" :disabled="verifyingSocialtoolsPages" >
                                <i v-if="verifyingSocialtoolsPages" class="fas fa-spinner fa-spin is-small"></i>
                                Verify Page IDs
                            </a>
                        </div>
                    </div>
                    <b-table
                        :data="socialtoolsPages"
                        :pagination-simple="false"
                    >
                        <template slot-scope="props">
                            <b-table-column field="socialNetwork" label="Network">
                                {{ props.row.socialNetwork }}
                            </b-table-column>
                            <b-table-column field="id" label="ID">
                                <b-input type="number" v-model="props.row.id" class="st-page-input" @change.native="stPageIdChanged(props.row)"></b-input>
                            </b-table-column>
                            <b-table-column field="name" label="Name">
                                {{ props.row.name }}
                            </b-table-column>
                            <b-table-column field="startDate" label="Start Date">
                                {{ props.row.startDate | formatDate }}
                            </b-table-column>
                            <b-table-column field="activeStatus" label="Status" centered>
                                <div v-if="props.row.activeStatus">
                                    <b-icon v-if="props.row.isActive" type="is-success" pack="fas" size="is-small" :title="props.row.activeStatus" icon="check"></b-icon>
                                    <b-icon v-if="!props.row.isActive" type="is-danger" pack="fas" size="is-small" :title="props.row.activeStatus" icon="times"></b-icon>
                                </div>
                            </b-table-column>
                        </template>
                    </b-table>
                </div>
            </section>
            <footer class="modal-card-foot">
                <button type="button" class="button" @click="cancel">Cancel</button>
                <button type="submit" class="button is-primary" :disabled="verifyingSocialtoolsPages  || saving || !isIncludedKeywordsValid || !isExcludedKeywordsValid">
                    <i v-if="saving" class="fas fa-spinner fa-spin is-small"></i>
                    <span class="small-padding-left">
                        {{ isUpdate ? 'Save' : 'Add' }}
                    </span>
                </button>
            </footer>
        </div>
    </form>
</template>

<script>
import NetBaseService from '@/services/netbase-service.js';
import SocialtoolsService from '@/services/socialtools-service.js';
import constants from '@/constants.js';
export default {
    name: 'BrandColumnForm',
    props: {
        updateColumnData: {
            type: Object,
            required: false
        },
        isUpdate: {
            type: Boolean,
            required: true
        },
        isPublished: {
            type: Boolean,
            required: true
        },
        initialColour: {
            type: String,
            required: false
        }
    },
    data () {
        return {
            columnData: {
                name: '',
                brandTopic: null,
                brandTheme: null,
                fbPageId: '',
                twPageId: '',
                ytPageId: '',
                igPageId: '',
                keywords: [],
                excludedKeywords: [],
                colour: ''
            },
            netBaseTopics: [],
            netBaseTopicInput: '',
            netBaseThemes: [],
            netBaseThemesInput: '',
            errorMessage: '',
            isNetBaseTopicsLoading: false,
            isNetBaseThemesLoading: false,
            verifyingSocialtoolsPages: false,
            saving: false,
            isIncludedKeywordsValid: true,
            isExcludedKeywordsValid: true,
        };
    },
    created () {
        if (this.updateColumnData) {
            this.columnData = Object.assign({}, this.updateColumnData);
            this.netBaseTopicInput = this.columnData.brandTopic ? this.columnData.brandTopic.name : this.netBaseTopicInput;
            this.netBaseThemesInput = this.columnData.brandTheme ? this.columnData.brandTheme.name : this.netBaseThemesInput;
        } else {
            this.columnData.colour = this.initialColour || '';
        }

        if (!this.isPublished) {
            let userId = localStorage.getItem('userId');
            this.isNetBaseTopicsLoading = true;
            this.isNetBaseThemesLoading = true;
            let isNetBaseAuthenticated = localStorage.getItem(
                'isNetBaseAuthenticated'
            );

            // localstorage only stores strings:  https://stackoverflow.com/questions/3263161/cannot-set-boolean-values-in-localstorage
            if (isNetBaseAuthenticated === 'true') {
                NetBaseService.getTopics(userId)
                    .then(response => {
                        this.netBaseTopics = response.data.map(topic => ({
                            id: topic.topicId,
                            name: topic.name
                        }));
                    })
                    .catch(error => {
                        this.errorMessage = error.message;
                    })
                    .finally(() => {
                        this.isNetBaseTopicsLoading = false;
                    });
                NetBaseService.getThemes(userId)
                    .then(response => {
                        this.netBaseThemes = [].concat([{ id: -1, name: 'None' }], response.data.map(theme => ({
                            id: theme.themeId,
                            name: theme.name
                        })));
                    })
                    .catch(error => {
                        this.errorMessage = error.message;
                    })
                    .finally(() => {
                        this.isNetBaseThemesLoading = false;
                    });
            } else {
                this.errorMessage = 'NetBase has not been authenticated';
                this.isNetBaseTopicsLoading = false;
                this.isNetBaseThemesLoading = false;
            }
        }
    },
    methods: {
        async saveColumn () {
            this.saving = true;
            let isSuccess = await this.verifySocialtoolsPages();
            this.saving = false;
            if (isSuccess) {
                // clear brand theme if set to None
                if (this.columnData.brandTheme && this.columnData.brandTheme.id <= 0) {
                    this.columnData.brandTheme = null;
                }
                updateSocialtoolsPages(this.columnData, this.socialtoolsPages);
                this.$emit(this.isPublished ? 'columnChanged' : 'columnSaved', this.columnData);
                this.$parent.close();
            }
        },
        cancel () {
            this.$parent.close();
        },
        async verifySocialtoolsPages () {
            let isVerificationSuccessfull = true;
            let socialtoolsPagesList = buildSTPageListToGetInfo(this.socialtoolsPages);
            if (socialtoolsPagesList.length > 0) {
                try {
                    this.verifyingSocialtoolsPages = true;
                    let response = await SocialtoolsService.getPagesInfo(socialtoolsPagesList);
                    if (response.data) {
                        response.data.forEach(o => {
                            let stPage = this.socialtoolsPages.find(p => p.id === o.id.toString() && p.socialNetwork === o.socialNetwork);
                            if (stPage) {
                                stPage.id = o.id;
                                stPage.name = o.name;
                                stPage.startDate = o.startDate;
                                stPage.activeStatus = o.activeStatus;
                                stPage.isActive = o.isActive;
                            }
                        });
                    }
                } catch (error) {
                    this.errorMessage = error.message;
                    isVerificationSuccessfull = false;
                } finally {
                    this.verifyingSocialtoolsPages = false;
                }
            }
            return isVerificationSuccessfull;
        },
        stPageIdChanged (item) {
            item.name = undefined;
            item.startDate = undefined;
            item.activeStatus = undefined;
            item.isActive = false;
        },
        verifyIncludedKeywords () {
            this.isIncludedKeywordsValid = !this.columnData.keywords.some(keyword => keyword.includes(':'));
        },
        verifyExcludedKeywords () {
            this.isExcludedKeywordsValid = !this.columnData.excludedKeywords.some(keyword => keyword.includes(':'));
        }
    },
    computed: {
        socialtoolsPages () {
            return buildSocialtoolsPages(this.columnData);
        },
        filteredNetBaseTopics() {
            return this.netBaseTopics.filter((topic) => {
                return topic.name
                    .toString()
                    .toLowerCase()
                    .indexOf(this.netBaseTopicInput.toLowerCase()) >= 0
                })
        },
        filteredNetBaseThemes() {
            return this.netBaseThemes.filter((theme) => {
                return theme.name
                    .toString()
                    .toLowerCase()
                    .indexOf(this.netBaseThemesInput.toLowerCase()) >= 0
                })
        }
    }
};

function buildSTPageListToGetInfo (socialtoolsPages) {
    return socialtoolsPages
        .filter((p) => p.id && !p.name)
        .map((p) => ({
            id: p.id,
            socialNetwork: p.socialNetwork
        }));
}

function buildSocialtoolsPages (columnData) {
    let createSTPageInfo = (id, socialNetwork, pageInfo) => {
        let newPageInfo = {
            id: id,
            socialNetwork: socialNetwork,
            name: undefined,
            startDate: undefined,
            activeStatus: undefined,
            isActive: false
        };
        return Object.assign(newPageInfo, pageInfo);
    };

    let fbPageInfo = createSTPageInfo(columnData.fbPageId, constants.ST_NETWORK_TYPE.FACEBOOK, columnData.fbPageInfo);
    let twPageInfo = createSTPageInfo(columnData.twPageId, constants.ST_NETWORK_TYPE.TWITTER, columnData.twPageInfo);
    let ytPageInfo = createSTPageInfo(columnData.ytPageId, constants.ST_NETWORK_TYPE.YOUTUBE, columnData.ytPageInfo);
    let igPageInfo = createSTPageInfo(columnData.igPageId, constants.ST_NETWORK_TYPE.INSTAGRAM, columnData.igPageInfo);
    let socialtoolsPages = [fbPageInfo, twPageInfo, ytPageInfo, igPageInfo];
    return socialtoolsPages;
}

function updateSocialtoolsPages (columnData, socialtoolsPages) {
    socialtoolsPages.forEach(pageInfo => {
        switch (pageInfo.socialNetwork) {
        case constants.ST_NETWORK_TYPE.FACEBOOK:
            columnData.fbPageId = pageInfo.id;
            columnData.fbPageInfo = pageInfo;
            break;
        case constants.ST_NETWORK_TYPE.TWITTER:
            columnData.twPageId = pageInfo.id;
            columnData.twPageInfo = pageInfo;
            break;
        case constants.ST_NETWORK_TYPE.YOUTUBE:
            columnData.ytPageId = pageInfo.id;
            columnData.ytPageInfo = pageInfo;
            break;
        case constants.ST_NETWORK_TYPE.INSTAGRAM:
            columnData.igPageId = pageInfo.id;
            columnData.igPageInfo = pageInfo;
            break;
        default:
            break;
        }
    });
}
</script>

<style scoped lang="scss">
.small-padding-left{
    padding-left: 3px;
}
</style>

<style lang="scss">
.project-name-input {
    max-width: 45rem;
}
.column-title-input {
    max-width: 40rem;
}
.st-page-input {
    width: 5rem;
}
.column-keywords-input {
    max-width: 50rem;
}
.modal-card-foot {
    justify-content: flex-end;
}
.socialtoolsPages {
    .title {
        margin-bottom: 10px;
    }
    .table {
        th:first-child, td:first-child {
            padding-left: 0;
        }
    }
    .level {
        margin-bottom: 0;
    }
}
</style>
